<template>
  <div>
    <div id="cyto" ref="cyto" :style="canvasStyle"></div>
  </div>
</template>

<script>
/* eslint-disable */
import coseBilkent from "cytoscape-cose-bilkent";
import layoutUtilities from "cytoscape-layout-utilities";
import cytoscape from "cytoscape";
// https://stackoverflow.com/questions/53652692/how-can-i-strore-the-cytoscape-object-inside-vue-js-data-instance

let cyInstance = null;

function createSeededRandom(seed) {
  return function() {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
}

export default {
  name: "RelationsCytoscape",
  data() {
    return {
      canvasSize: 0,
      aspectRatio: 0.8,
      cyIsInitialized: false
    };
  },
  props: ["initialCanvasWidth", "selectedCaseNumber"],
  created() {
    this.canvasSize = this.initialCanvasWidth;
  },
  computed: {
    canvasStyle() {
      return {
        width: this.canvasSize + "px",
        height: this.canvasSize * this.aspectRatio + "px"
      };
    }
  },
  mounted() {
    this.$bus.$on("relations-width-change", newWidth => {
      if (newWidth > 0) {
        const previousCanvasSize = this.canvasSize;
        this.canvasSize = newWidth;

        this.$nextTick(() => {
          if (this.cyIsInitialized && cyInstance) {
            if (previousCanvasSize !== newWidth || !cyInstance.width() || (this.$refs.cyto && cyInstance.width() !== this.$refs.cyto.offsetWidth) ) {
                 cyInstance.resize();
                 cyInstance.fit(20);
            }
          } else if (!this.cyIsInitialized) {
            this.initializeCytoscape();
          }
        });
      } else if (newWidth <= 0 && this.canvasSize !== newWidth) {
         this.canvasSize = newWidth;
      }
    });

    this.initializeCytoscape();
  },
  beforeDestroy() {
    this.$bus.$off("relations-width-change");
    if (cyInstance) {
      cyInstance.destroy();
      cyInstance = null;
    }
    this.cyIsInitialized = false;
  },
  methods: {
    initializeCytoscape() {
      if (this.cyIsInitialized) {
        return;
      }

      this.$nextTick(() => {
        if (this.$refs.cyto && this.$refs.cyto.offsetWidth > 0 && this.canvasSize > 0) {
          cytoscape.use(coseBilkent);
          const elementsForCy = this.generateElements();

          let cy = cytoscape({
            container: this.$refs.cyto,
            elements: elementsForCy,
            style: this.generateStyle(),
            autounselectify: true,
            userPanningEnabled: false,
            userZoomingEnabled: false,
            boxSelectionEnabled: false,
            autoungrabify: true
          });

          cyInstance = cy;
          this.cyIsInitialized = true;

          const seededRandom = createSeededRandom(12345);
          cy.nodes().forEach(node => {
            node.position({
              x: seededRandom() * this.canvasSize,
              y: seededRandom() * this.canvasSize * this.aspectRatio
            });
          });

          cy.layout({
            name: "cose-bilkent",
            animate: false, idealEdgeLength: 50, quality: "proof", randomize: false,
            nodeDimensionsIncludeLabels: false, nodeRepulsion: 8000, edgeElasticity: 0.45,
            nestingFactor: 0.1, numIter: 100000, gravity: 0.25, tile: true,
            fit: true, positions: (node) => node.position()
          }).run();

          cy.style().selector("core").css({ "active-bg-size": 0 });

          var packingApi = cy.layoutUtilities({
            desiredAspectRatio: 1 / this.aspectRatio,
            polyominoGridSizeFactor: 1, utilityFunction: 1, componentSpacing: 0
          });

          var components = cy.elements(":visible").components();
          var subgraphs = [];
          if (components.length > 0) {
            components.forEach(function(component) {
              var subgraph = {};
              subgraph.nodes = []; subgraph.edges = [];
              component.edges().forEach(function(edge) {
                var boundingBox = edge.boundingBox();
                subgraph.edges.push({
                  startX: boundingBox.x1, startY: boundingBox.y1,
                  endX: boundingBox.x2, endY: boundingBox.y2
                });
              });
              component.nodes().forEach(function(node) {
                var boundingBox = node.boundingBox();
                subgraph.nodes.push({
                  x: boundingBox.x1, y: boundingBox.y1,
                  width: boundingBox.w, height: boundingBox.h
                });
              });
              subgraphs.push(subgraph);
            });
          }

          if (this.$store.state.main.allPlayers.length > 2 && subgraphs.length > 0 && components.length > 0) {
            try {
              var result = packingApi.packComponents(subgraphs);
              if (result && result.shifts) {
                components.forEach(function(component, index) {
                  if (result.shifts[index]) {
                    component.nodes().layout({
                      name: "preset", animate: false, fit: false,
                      transform: node => {
                        let position = {};
                        position.x = node.position("x") + result.shifts[index].dx;
                        position.y = node.position("y") + result.shifts[index].dy;
                        return position;
                      }
                    }).run();
                  }
                });
              }
            } catch (e) {
              // Virheenkäsittely tuotantokoodissa voisi olla esimerkiksi virheen logitus palvelimelle
              // console.error("Error during packing components:", e);
            }
          }

          cy.$("node").forEach(node => { node.style("events", "no"); });
          cy.$("edge").forEach(node => { node.style("events", "no"); });

          var layout = cy.layout({
            name: "preset", fit: true, animate: false, padding: 20
          });
          layout.run();
        }
      });
    },

    generateElements() {
      var elements = [];
      this.$store.state.main.allPlayers.forEach(player => {
        let label = this.generateLabel(player);
        let newElement = {
          data: {
            id: player.id,
            label: label,
            type: "noselfevaluation"
          }
        };
        const thisCaseEvaluation = this.$store.getters.getSomePlayersSelfEvaluationsForCase(
          player.id,
          this.selectedCaseNumber
        );
        if (thisCaseEvaluation !== undefined) {
          newElement.data.type = thisCaseEvaluation.priority - 1;
        }
        elements.push(newElement);
      });

      const thisCaseNominations = this.$store.getters.getNominationsForCase(
        this.selectedCaseNumber
      );

      if (thisCaseNominations.length > 0) {
        thisCaseNominations.forEach(nomination => {
          const nominatorPlayer = this.$store.getters.getPlayerById(nomination.player);
          const nomineePlayer = this.$store.getters.getPlayerById(nomination.nominee);

          if (!nominatorPlayer || !nomineePlayer) {
            return;
          }

          const newEdgeElement = {
            data: {
              id: "l" + nomination.id,
              source: nomination.player,
              target: nomination.nominee
            }
          };
          elements.push(newEdgeElement);
        });
      }
      return elements;
    },

    generateStyle() {
      const nodeBackgroundColors = this.generateColorStyles();
      return [
        {
          selector: "node",
          style: {
            "font-family": "Poppins, sans-serif",
            "font-weight": 600,
            "text-outline-color": "white",
            "text-outline-width": 1,
            "text-outline-opacity": "0.3",
            shape: "round-rectangle",
            width: "label",
            height: "30px",
            padding: "5px",
            "text-valign": "center",
            "text-halign": "center",
            "text-wrap": "wrap",
            "text-max-width": "140px",
            "text-margin-y": 2
          }
        },
        {
          selector: "node[label]",
          style: {
            label: "data(label)"
          }
        },
        {
          selector: "edge",
          style: {
            width: 2,
            "line-color": "#999999",
            "target-arrow-color": "#999999",
            "target-arrow-shape": "triangle",
            "curve-style": "bezier",
            "loop-direction": "0deg",
            "loop-sweep": "90deg"
          }
        },
        ...nodeBackgroundColors
      ];
    },

    generateLabel(player) {
      if (!this.$store.state.main.showPriorityLabels) {
        return player.nick_name;
      }
      const thisCaseEvaluation = this.$store.getters.getSomePlayersSelfEvaluationsForCase(
        player.id, this.selectedCaseNumber
      );
      if (!thisCaseEvaluation) { return player.nick_name; }
      const priority = this.$store.getters.getNumberOfCases - thisCaseEvaluation.priority + 1;
      return player.nick_name + "(" + priority + ")";
    },

    generateColorStyles() {
      var colors = [];
      if (this.$store.state.main.colors && this.$store.state.main.textColors) {
        this.$store.state.main.colors.forEach((color, i) => {
          var newColor = {};
          newColor.selector = "node[type=" + i + "]";
          newColor.style = { "background-color": color, color: this.$store.state.main.textColors[i] };
          colors.push(newColor);
        });
      }
      var noSelfEvaluationColor = {
        selector: "node[type='noselfevaluation']",
        style: { "background-color": "white", color: "black" }
      };
      colors.push(noSelfEvaluationColor);
      return colors;
    },
     setPadding() {
       // Metodi oli olemassa, mutta ei käytössä.
     }
  }
};
</script>